import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Formik, Field, Form } from 'formik';
import _ from 'lodash';
import DatePicker from 'react-datepicker';
import { DateTime } from 'luxon';
import moment from 'moment';
import { RRule } from 'rrule';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles } from '@material-ui/core/styles';

import AmountField from 'components/QuickenControls/AmountField';
import QChoiceChips from 'components/QuickenControls/QChoiceChips';
import { frequencyAliases, frequencyAliasFromRecurrence, recurrenceFromFrequencyAlias, negatorFromCOA, rruleFromRecurrence } from 'data/scheduledTransactions/scheduledTransactionsUtils';
import { deleteScheduledTransaction, updateScheduledTransaction } from 'data/scheduledTransactions/scheduledTransactionsActions';
import { mkScheduledTransaction, mkRecurrence } from 'data/scheduledTransactions/scheduledTransactionsTypes';
import { CashFlowTransaction } from 'data/transactions/types';

const useStyles = makeStyles((_theme) => ({
  amountField: {
    textAlign: 'left',
  },
}));

function initialValues(scheduledTransaction) {
  const negator = negatorFromCOA(scheduledTransaction.transaction && scheduledTransaction.transaction.coa);
  const virtualShowInitialSign = scheduledTransaction.transaction && scheduledTransaction.transaction.amount ? Math.sign(scheduledTransaction.transaction.amount) !== negator : undefined;
  const virtualFrequency = frequencyAliasFromRecurrence(scheduledTransaction.recurrence);
  const amount = scheduledTransaction.transaction && !Number.isNaN(scheduledTransaction.transaction.amount) ?
    String(negator < 0 ? Math.abs(scheduledTransaction.transaction.amount) : scheduledTransaction.transaction.amount) :
    undefined;
  const dueOnDate = DateTime.fromISO(scheduledTransaction.dueOn, { zone: 'utc' });

  let virtualBaseDate;
  if (dueOnDate && scheduledTransaction.recurrence) {
    try {
      const ruleBack = new RRule({
        tzid: 'utc',
        ...rruleFromRecurrence(scheduledTransaction.recurrence),
        dtstart: dueOnDate.minus({ years: 1 }).toJSDate(),
        until: dueOnDate.toJSDate(),
      });
      virtualBaseDate = ruleBack.before(dueOnDate.toJSDate(), false);
    } catch (e) {
      assert(false, e); // bugsnag investigation
    }
  }

  return {
    ...scheduledTransaction,
    dueOn: dueOnDate && dueOnDate.isValid ? dueOnDate.toLocaleString(DateTime.DATE_SHORT) : '',
    transaction: {
      ...scheduledTransaction.transaction,
      amount,
    },
    virtualShowInitialSign,
    virtualFrequency,
    virtualBaseDate,
  };
}

const ScheduledTransactionLightForm = (props) => {
  const { submitted, renderProps } = props;

  const classes = useStyles();

  const dispatch = useDispatch();

  const renderPropsCallback = useCallback((formikProps) => {
    if (renderProps) {
      renderProps({
        remove: () => {
          dispatch(deleteScheduledTransaction(mkScheduledTransaction({
            id: formikProps.values.id,
            isDeleted: true,
          })));
          if (submitted) {
            submitted();
          }
        },
        submit: () => {
          if (formikProps.dirty) {
            formikProps.submitForm();
          } else if (submitted) {
            submitted();
          }
        },
      });
    }
  }, [renderProps, dispatch, submitted]);

  return (
    <Formik
      key={props.scheduledTransaction && props.scheduledTransaction.id}
      initialValues={initialValues(props.scheduledTransaction)}
      validateOnChange
      onSubmit={(values, _actions) => {
        const dueOnMoment = values.dueOn && moment(values.dueOn);
        const dueOn = dueOnMoment && dueOnMoment.isValid() ? dueOnMoment.format('YYYY-MM-DD') : undefined;
        const recurrence = recurrenceFromFrequencyAlias(values.virtualFrequency, dueOn);
        const negator = negatorFromCOA(values.transaction.coa);
        const amount = (values.transaction.amount.includes('+') || values.transaction.amount.includes('-')) ? Number(values.transaction.amount) : Number(values.transaction.amount) * negator;
        dispatch(updateScheduledTransaction(mkScheduledTransaction({
          ...values,
          dueOn,
          recurrence: mkRecurrence(recurrence),
          transaction: new CashFlowTransaction({
            ...values.transaction,
            amount,
          }),
        })));
        if (props.submitted) {
          props.submitted();
        }
      }}
    >
      {(formikProps) =>
        <Form>
          <Grid container spacing={4}>

            <Grid item xs={12}>
              <Field
                name="transaction.payee"
                validate={(value) => value && value.length > 0 ? undefined : 'required'}
              >
                {({field, form: {touched, errors}}) =>
                  <TextField
                    {...field}
                    id="payee"
                    label="Name"
                    error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                    helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                    InputProps={{
                      autoComplete: 'off',
                    }}
                    margin="dense"
                    fullWidth
                    autoFocus
                    onFocus={(event) => event.target.select()}
                  />
                }
              </Field>
            </Grid>

            <Grid item xs={12}>
              <Field
                name="transaction.amount"
                validate={(value) => value === undefined || value === null || value === '' ? 'required' : undefined}
              >
                {({ field, form: { touched, errors, values } }) => (
                  <AmountField
                    {...field}
                    showInitialSign={values.virtualShowInitialSign}
                    showAmountAdornment
                    editable
                    omitCents={false}
                    currencySymbol="USD"
                    amountType="amount"
                    id="amount"
                    label="Amount"
                    margin="dense"
                    fullWidth
                    autoComplete="off"
                    error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                    helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                    className={classes.amountField}
                  />
                )}
              </Field>
            </Grid>

            <Grid item xs={12}>
              <Field
                name="virtualFrequency"
                validate={(value) => value === undefined || value === null || value === '' ? 'required' : undefined}
              >
                {({ field, form: { values, touched, errors } }) => (
                  <FormControl
                    {...field}
                    error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                  >
                    <InputLabel shrink>Frequency</InputLabel>
                    <Input
                      id="frequency"
                      multiline
                      disableUnderline
                      inputComponent={QChoiceChips}
                      inputProps={{
                        items: frequencyAliases,
                        selectedItems: field.value ? [field.value] : [],
                        onSelected: (items) => {
                          const frequency = items && items.length === 1 ? items[0] : undefined;
                          if (frequency) {
                            if (!touched.dueOn && values.virtualBaseDate) {
                              const luxonBaseDate = DateTime.fromJSDate(values.virtualBaseDate);
                              const isoBaseDate = luxonBaseDate.toUTC().toISODate();
                              const recurrence = recurrenceFromFrequencyAlias(frequency, isoBaseDate);
                              if (recurrence && luxonBaseDate) {
                                const projectedDate = luxonBaseDate.plus({ years: 1}).toJSDate();
                                const ruleForward = new RRule({
                                  tzid: 'utc',
                                  ...rruleFromRecurrence(recurrence),
                                  dtstart: values.virtualBaseDate,
                                  until: projectedDate,
                                });
                                const jsTodayUTCDate = DateTime.local()
                                  .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                                  .setZone('utc', { keepLocalTime: true })
                                  .toJSDate();
                                const jsDueOn = ruleForward.after(jsTodayUTCDate, true);
                                if (jsDueOn) {
                                  const luxonDueOn = DateTime.fromJSDate(jsDueOn);
                                  const localeDueOn = luxonDueOn.toUTC().toLocaleString(DateTime.DATE_SHORT);
                                  formikProps.setFieldValue('dueOn', localeDueOn);
                                }
                              }
                            }
                            formikProps.setFieldValue('virtualFrequency', frequency);
                          }
                        },
                      }}
                    />
                    {_.get(touched, field.name) && _.get(errors, field.name) &&
                    <FormHelperText id="frequency-helper-text">
                      {_.get(errors, field.name)}
                    </FormHelperText>
                    }
                  </FormControl>
                )}
              </Field>
            </Grid>

            <Grid item xs={12}>
              <Field
                name="dueOn"
                validate={(value) => {
                  let errorMessage;
                  if (!value) {
                    errorMessage = 'required';
                  } else if (formikProps.initialValues.dueOn !== value) {
                    const dueOn = moment(value);
                    const now = moment().startOf('date');
                    if (!dueOn.isValid()) {
                      errorMessage = 'invalid date format';
                    } else if (dueOn < now) {
                      errorMessage = "can't change to past dates";
                    } else if (formikProps.values.recurrence && formikProps.values.recurrence.endOn) {
                      const endOn = moment(formikProps.values.recurrence.endOn);
                      if (endOn.isValid() && dueOn > endOn) {
                        errorMessage = `can't use dates after ${formikProps.values.recurrence.endOn}`;
                      }
                    }
                  }
                  return errorMessage;
                }}
              >
                {({ field, form: { values, touched, errors } }) => {
                  const date = moment(field.value);
                  const selected = date.isValid() ? date : moment();
                  return (
                    <DatePicker
                      id="next-date"
                      selected={selected}
                      onChange={(momentDate) => {
                        if (momentDate) {
                          formikProps.setFieldValue(field.name, momentDate.format('l'));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.stopPropagation();
                        }
                      }}
                      minDate={moment().toDate()}
                      maxDate={values.recurrence && values.recurrence.endOn ? moment(values.recurrence.endOn).toDate() : undefined}
                      allowSameDay
                      disabledKeyboardNavigation
                      customInputRef="inputRef"
                      customInput={
                        <TextField
                          InputProps={{
                            ...field,
                            autoComplete: 'off',
                          }}
                          label="Next Date"
                          margin="dense"
                          fullWidth
                          error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                          helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                        />
                      }
                    />
                  );
                }}
              </Field>
            </Grid>

            {renderPropsCallback ? renderPropsCallback(formikProps) : null}

          </Grid>
        </Form>
      }
    </Formik>
  );
};

ScheduledTransactionLightForm.propTypes = {
  scheduledTransaction: PropTypes.object,
  renderProps: PropTypes.func,
  submitted: PropTypes.func,
};

export default ScheduledTransactionLightForm;
